import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar, Badge, Logo, ThemeToggle } from "@promptgatewaysadmin/ui";
import { useSession } from "../auth/SessionProvider.js";
import { useAuth } from "../auth/AuthContext.js";
import { StatusPill } from "../components/StatusPill.js";
import { PAGE_TITLES } from "../router/titles.js";
import { config } from "../config.js";

interface NavEntry {
  to: string;
  label: string;
  adminOnly: boolean;
  /** Hidden on the individual plan — there is no team to manage. */
  teamOnly: boolean;
}

const NAV: readonly NavEntry[] = [
  { to: "/", label: "Home", adminOnly: false, teamOnly: false },
  { to: "/onboarding", label: "Install extension", adminOnly: false, teamOnly: false },
  { to: "/account", label: "Account", adminOnly: false, teamOnly: false },
  { to: "/settings", label: "Settings", adminOnly: false, teamOnly: false },
  { to: "/team", label: "Team", adminOnly: false, teamOnly: true },
  { to: "/billing", label: "Billing", adminOnly: true, teamOnly: false },
  { to: "/subscription", label: "Subscription", adminOnly: true, teamOnly: false },
];

/**
 * Archetype B — a 230px sidebar, a 60px sticky header, and a content column.
 *
 * Nav items are real <a href> with aria-current="page" on the active one. The mock used
 * <button onClick>, which is the single highest-impact defect carried over from the
 * marketing site: it breaks middle-click, cmd-click, "copy link", and every assistive
 * technology's idea of what a navigation region is.
 *
 * Billing and Subscription are HIDDEN from a member's sidebar — a nav item that always
 * refuses is noise. The route guard is what actually stops a member who types the URL.
 * Both exist; neither is the enforcement. Team is hidden the same way on the individual
 * plan (RequireTeamPlan is the matching guard).
 */
export function AppShell() {
  const session = useSession();
  const location = useLocation();
  const isAdmin = session.role === "admin";
  const individual = session.plan === "individual";
  const items = NAV.filter((n) => (!n.adminOnly || isAdmin) && (!n.teamOnly || !individual));
  const title = PAGE_TITLES[location.pathname] ?? "Home";

  return (
    <div className="pg-shell">
      <a href="#main" className="pg-skip">
        Skip to main content
      </a>

      <aside className="pg-shell__rail">
        <div className="pg-shell__brand">
          <Link to="/" aria-label="Prompt Gateway — home">
            <Logo />
          </Link>
        </div>
        <nav aria-label="Main">
          <ul className="pg-shell__nav">
            {items.map((item) => (
              <li key={item.to}>
                {/* NavLink renders a real <a href> and sets aria-current="page" itself. */}
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    isActive ? "pg-navitem pg-navitem--active" : "pg-navitem"
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <UserChip />
        <a className="pg-navitem" href={config.siteOrigin}>
          promptgateways.com
        </a>
      </aside>

      <div className="pg-shell__col">
        <header className="pg-shell__header">
          <h1 className="pg-shell__title">{title}</h1>
          <div className="pg-shell__slot">
            <StatusPill />
            <ThemeToggle />
          </div>
        </header>
        <main id="main" tabIndex={-1} className="pg-shell__main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * The sidebar user chip. The mock's chip is not interactive — a portal with no visible
 * sign-out is a defect, so the menu exists and Log out is in it.
 *
 * There is no /logout route: a URL that terminates a session on GET is a CSRF footgun.
 */
function UserChip() {
  const session = useSession();
  const auth = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const name = session.me.displayName || session.me.email;

  return (
    <div className="pg-shell__chip" ref={ref}>
      <button
        type="button"
        className="pg-shell__chipbtn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Avatar name={name} size="sm" />
        <span className="pg-shell__chipname">{name}</span>
        {session.orgName && (
          <Badge tone="neutral" className="pg-shell__chiporg">
            {session.orgName}
          </Badge>
        )}
      </button>
      {open && (
        <ul className="pg-shell__menu" role="menu">
          <li role="none">
            <NavLink role="menuitem" to="/account" onClick={() => setOpen(false)}>
              Account
            </NavLink>
          </li>
          <li role="none">
            <NavLink role="menuitem" to="/settings" onClick={() => setOpen(false)}>
              Settings
            </NavLink>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              onClick={async () => {
                await auth.signOut();
                // Not optional: a cached /me or invoice list left behind means the next
                // user on a shared machine sees the previous user's org for one frame.
                qc.clear();
                navigate("/login", { replace: true });
              }}
            >
              Log out
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

